import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FaSearch, FaFilter, FaTimes, FaSortAlphaDown, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { API } from '../App';
import RecipeCard from '../components/RecipeCard';
import SponsoredRowBlock from '../components/SponsoredRowBlock';
import AdvertiseYourselfCTA from '../components/AdvertiseYourselfCTA';
import { showsInFeedSponsorRows } from '../utils/adVisibility';
import UpgradeModal from '../components/UpgradeModal';
import AIRecipeGeneratorModal from '../components/AIRecipeGeneratorModal';
import { useAuth } from '../context/AuthContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import { getRecipesPageSteps, isTourCompleted, markTourCompleted, TOUR_KEYS } from '../utils/onboarding';
import { getUserLanguage } from '../utils/geolocation';
import { useTranslation } from 'react-i18next';
import { useFrozenNewRecipes } from '../hooks/useNewRecipes';
import { matchesSearchTerm } from '../utils/recipeFilter';

import { hasPremiumAccess } from '../config/subscriptions';
const RecipesPage = ({ sessionId }) => {
  const { user, updateCompletedTours } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Smart-search state: server-side suggestions when the local filter returns 0
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [smartLowConfidence, setSmartLowConfidence] = useState([]);
  const [smartAiEligible, setSmartAiEligible] = useState(false);
  const [smartLoading, setSmartLoading] = useState(false);
  // Returned by /api/recipes/smart-search when matches=0; lets us mark the
  // empty-search row as ai_clicked=true if the user follows through with AI.
  const [smartSearchLogId, setSmartSearchLogId] = useState(null);
  // AI-recipe-generator modal: opens when user clicks the AI CTA in the
  // 0-result state. The modal calls /api/ai/create-recipe and lets the
  // user save the draft privately or send it to the Sandbox.
  const [aiModalOpen, setAiModalOpen] = useState(false);
  // Load sortBy from localStorage or use default
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('recipeSortBy') || 'default';
  });
  // Load filters from localStorage or use defaults
  const [alcoholFilter, setAlcoholFilter] = useState(() => {
    return localStorage.getItem('recipeAlcoholFilter') || 'both';
  });
  const [typeFilter, setTypeFilter] = useState(() => {
    return localStorage.getItem('recipeTypeFilter') || '';
  });
  const [showMyRecipes, setShowMyRecipes] = useState(false);
  const [showNewOnly, setShowNewOnly] = useState(false);  // "Vis kun nye" toggle
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);  // mobile-only filter drawer
  const [currentTourStep, setCurrentTourStep] = useState(-1);
  const tourInitialized = useRef(false);
  
  // Ingredient filters
  const [includeIngredients, setIncludeIngredients] = useState([]);
  const [excludeIngredients, setExcludeIngredients] = useState([]);
  const [ingredientInput, setIngredientInput] = useState('');
  const [excludeIngredientInput, setExcludeIngredientInput] = useState('');
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [showIncludeSuggestions, setShowIncludeSuggestions] = useState(false);
  const [showExcludeSuggestions, setShowExcludeSuggestions] = useState(false);
  const [isIngredientFilterExpanded, setIsIngredientFilterExpanded] = useState(false);

  // Reklame-blokke (fuld-bredde sponsor-rækker) — vises inde i opskrifts-
  // griddet fordelt jævnt baseret på antallet af aktive sponsor-rækker
  // (1 sponsor → midten, 2 → 1/3+2/3, N → jævnt fordelt). Rækkefølgen
  // roterer pr. sidevisning via en offset i localStorage så alle
  // annoncører får omtrent samme eksponering over tid. Rotationen sker
  // ÉN gang når data ankommer og ændrer sig IKKE mens brugeren læser.
  // Visibility styres centralt af `showsInFeedSponsorRows(user)` (pt. kun
  // LIGHT-tier). Gæst/Free ser bundreklamer i stedet (håndteret af <AdSlot/>).
  const [sponsoredRows, setSponsoredRows] = useState([]);
  const [presentedSponsoredRows, setPresentedSponsoredRows] = useState([]);
  const shouldShowAds = showsInFeedSponsorRows(user);
  const SPONSOR_ROTATION_STORAGE_KEY = 'slushbook_sponsor_rotation_offset_v1';

  // "Nye opskrifter siden sidste besøg" — freezes the "last seen"
  // watermark for this visit so badges/filter stay stable while user
  // browses, then bumps it to "now" on unmount.
  const { frozenSince, isNew: isNewRecipe, newCount } = useFrozenNewRecipes(recipes);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('recipeAlcoholFilter', alcoholFilter);
  }, [alcoholFilter]);

  useEffect(() => {
    localStorage.setItem('recipeTypeFilter', typeFilter);
  }, [typeFilter]);

  useEffect(() => {
    localStorage.setItem('recipeSortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    fetchRecipes();
    // eslint-disable-next-line
  }, [sessionId, alcoholFilter, typeFilter, sortBy, includeIngredients, excludeIngredients, user?.interests?.join(',')]);

  // Hent sponsor-rækker én gang for brugere der ser reklamer.
  useEffect(() => {
    if (!shouldShowAds) {
      setSponsoredRows([]);
      return;
    }
    let cancelled = false;
    // Country-detektion konsistent med AdSlot.js (default DK).
    const detectCountry = () => {
      const lang = (typeof navigator !== 'undefined' && navigator.language) || '';
      const map = {
        da: 'DK', 'da-DK': 'DK',
        de: 'DE', 'de-DE': 'DE',
        'en-GB': 'GB', 'en-US': 'US',
        fr: 'FR', 'fr-FR': 'FR',
      };
      return map[lang] || 'DK';
    };
    (async () => {
      try {
        const res = await axios.get(`${API}/sponsored-rows`, {
          params: { country: detectCountry() },
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setSponsoredRows(rows);
      } catch (err) {
        console.error('[RecipesPage] Failed to fetch sponsored rows:', err);
        if (!cancelled) setSponsoredRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldShowAds]);

  // Rotér sponsor-rækkerne ÉN gang pr. sidevisning når data ankommer.
  // - localStorage-offset inkrementeres pr. mount så næste besøg starter
  //   fra en anden sponsor (fair round-robin eksponering).
  // - `presentedSponsoredRows` beregnes ÉN gang og ændres ikke igen mens
  //   brugeren scroller/filtrerer, så bannere flytter sig aldrig under
  //   læsning.
  useEffect(() => {
    if (!Array.isArray(sponsoredRows) || sponsoredRows.length === 0) {
      setPresentedSponsoredRows([]);
      return;
    }
    let offset = 0;
    try {
      const raw = localStorage.getItem(SPONSOR_ROTATION_STORAGE_KEY);
      offset = Number.parseInt(raw || '0', 10);
      if (!Number.isFinite(offset)) offset = 0;
    } catch (e) {
      offset = 0;
    }
    const len = sponsoredRows.length;
    const safeOffset = ((offset % len) + len) % len;
    const rotated = [
      ...sponsoredRows.slice(safeOffset),
      ...sponsoredRows.slice(0, safeOffset),
    ];
    setPresentedSponsoredRows(rotated);
    try {
      localStorage.setItem(
        SPONSOR_ROTATION_STORAGE_KEY,
        String((safeOffset + 1) % len)
      );
    } catch (e) {
      // localStorage kan være blokeret i inkognito — rotation degraderes
      // til "altid start fra første sponsor". Ikke fatalt.
    }
    // Depend on the reference of sponsoredRows so rotation kun sker når
    // fetch faktisk gav nye data — ikke på hver render.
  }, [sponsoredRows]);

  useEffect(() => {
    filterRecipes();
  }, [recipes, searchTerm, showMyRecipes, showNewOnly]);

  // Extract unique ingredients from all recipes for autocomplete
  useEffect(() => {
    if (recipes.length > 0) {
      const allIngredients = new Set();
      recipes.forEach(recipe => {
        if (recipe.ingredients) {
          recipe.ingredients.forEach(ing => {
            if (ing.name) {
              allIngredients.add(ing.name.toLowerCase());
            }
          });
        }
      });
      setAvailableIngredients(Array.from(allIngredients).sort());
    }
  }, [recipes]);

  // Start tour if coming from HomePage and tour not completed
  useEffect(() => {
    if (hasPremiumAccess(user) && !isTourCompleted(TOUR_KEYS.RECIPES, user) && !tourInitialized.current && recipes.length > 0) {
      console.log('[Tour] Starting recipes tour...');
      tourInitialized.current = true;
      setTimeout(() => {
        setCurrentTourStep(0);
      }, 1500);
    }
  }, [user, recipes]);

  const handleTourNext = () => {
    const nextStep = currentTourStep + 1;
    setCurrentTourStep(nextStep);
  };

  const handleTourSkip = () => {
    markTourCompleted(TOUR_KEYS.RECIPES, API, updateCompletedTours);
    setCurrentTourStep(-1);
  };

  const handleTourFinish = () => {
    markTourCompleted(TOUR_KEYS.RECIPES, API, updateCompletedTours);
    setCurrentTourStep(-1);
    
    // Navigate to Add Recipe page to continue tour
    setTimeout(() => {
      navigate('/add-recipe');
    }, 500);
  };

  
  // Check URL params for sort preference
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort');
    if (sortBy === 'rating') {
      // Sort by rating when coming from "Højest vurderet" button
      const sorted = [...recipes].sort((a, b) => {
        // Recipes WITH ratings first
        const aHasRating = (a.rating_avg || 0) > 0;
        const bHasRating = (b.rating_avg || 0) > 0;
        if (aHasRating && !bHasRating) return -1;
        if (!aHasRating && bHasRating) return 1;
        
        // Then by rating value
        const aRating = a.rating_avg || 0;
        const bRating = b.rating_avg || 0;
        return bRating - aRating;
      });
      setRecipes(sorted);
    }
  }, []);

  const fetchRecipes = async () => {
    try {
      const params = new URLSearchParams({
        session_id: sessionId,
        alcohol: alcoholFilter,
        lang: getUserLanguage()  // Add language parameter
      });
      if (typeFilter) params.append('type', typeFilter);
      if (includeIngredients.length > 0) {
        params.append('include_ingredients', includeIngredients.join(','));
      }
      if (excludeIngredients.length > 0) {
        params.append('exclude_ingredients', excludeIngredients.join(','));
      }
      
      const response = await axios.get(`${API}/recipes?${params}`);
      
      // Check if we should sort by rating from URL
      const urlParams = new URLSearchParams(window.location.search);
      const sortByParam = urlParams.get('sort');
      
      let sortedRecipes;
      
      // Sort based on sortBy state or URL param
      const activeSortBy = sortByParam || sortBy;
      
      if (activeSortBy === 'alphabetical') {
        // Sort alphabetically by name (A-Z)
        sortedRecipes = response.data.sort((a, b) => 
          a.name.localeCompare(b.name, 'da')
        );
      } else if (activeSortBy === 'rating') {
        // Sort by rating (highest first), prioritizing recipes WITH ratings
        sortedRecipes = response.data.sort((a, b) => {
          // Recipes WITH ratings first
          const aHasRating = (a.rating_avg || 0) > 0;
          const bHasRating = (b.rating_avg || 0) > 0;
          if (aHasRating && !bHasRating) return -1;
          if (!aHasRating && bHasRating) return 1;
          
          // Then sort by rating value (highest first)
          const aRating = a.rating_avg || 0;
          const bRating = b.rating_avg || 0;
          return bRating - aRating;
        });
      } else {
        // Default sort: free recipes first, then own recipes, then by created date
        sortedRecipes = response.data.sort((a, b) => {
          // Free recipes first (is_free=true or missing is_free field means free)
          const aIsFree = a.is_free !== false;
          const bIsFree = b.is_free !== false;
          if (aIsFree && !bIsFree) return -1;
          if (!aIsFree && bIsFree) return 1;
          
          // Then own recipes
          const aIsOwn = a.author === sessionId;
          const bIsOwn = b.author === sessionId;
          if (aIsOwn && !bIsOwn) return -1;
          if (!aIsOwn && bIsOwn) return 1;
          
          // Then sort by created date (newest first)
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
      }
      
      // ---- Interest-based pre-sort (does NOT filter) ----
      // Boost recipes that match one of the user's selected interests so they
      // appear FIRST. The mapping mirrors EXACTLY what the category filter
      // chips on this page do (backend `?type=...` is a direct `recipe.type`
      // equality match) so picking "Kaffe / Frappé" in onboarding boosts the
      // same set as clicking the "Kaffe" filter chip. All recipes remain
      // visible — this is a stable PARTITION, not a filter.
      const interestTypes = {
        // "Slushice" covers the platform's classic slush types
        slushice: new Set(['klassisk', 'juice', 'sukkerfri', 'sport', 'sodavand']),
        // Onboarding label "Kaffe / Frappé"  ⇔  filter chip "Kaffe"
        frappe: new Set(['kaffe']),
        // Onboarding label "Mælk / Milkshake"  ⇔  filter chip "Mælk"
        milkshake: new Set(['maelk', 'mælk', 'smoothie']),
        // Onboarding label "Cocktail / Frozen Cocktails"  ⇔  filter chip "Cocktail"
        frozen_cocktails: new Set(['cocktail']),
      };

      const userInterests = user?.interests || [];
      if (userInterests.length > 0) {
        const boostedTypes = new Set();
        for (const i of userInterests) {
          (interestTypes[i] ? Array.from(interestTypes[i]) : []).forEach((t) => boostedTypes.add(t));
        }
        // Stable partition: matches first, rest after — order within each group preserved
        const matches = [];
        const rest = [];
        for (const r of sortedRecipes) {
          const rtype = (r?.type || '').toLowerCase();
          if (rtype && boostedTypes.has(rtype)) matches.push(r);
          else rest.push(r);
        }
        // Debug aid: log once how many recipes were matched. Helps verify the
        // partition is taking effect in production.
        // eslint-disable-next-line no-console
        console.log(
          `[Interests] sorted ${matches.length}/${sortedRecipes.length} recipes to top for interests=${JSON.stringify(userInterests)} (types=${Array.from(boostedTypes).join(',')})`
        );
        sortedRecipes = [...matches, ...rest];
      }

      setRecipes(sortedRecipes);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRecipes = () => {
    let filtered = recipes;
    
    // Filter by "mine opskrifter" if enabled
    if (showMyRecipes) {
      filtered = filtered.filter(recipe => recipe.author === sessionId);
    }

    // Filter by "kun nye siden sidste besøg" if enabled
    if (showNewOnly) {
      filtered = filtered.filter(recipe => isNewRecipe(recipe));
    }

    // Filter by search term. Delegates to matchesSearchTerm() so the
    // alias-inclusive contract is unit-tested in isolation (see
    // utils/recipeFilter.test.js — pins the Icebreaker "sports" alias bug).
    if (searchTerm) {
      filtered = filtered.filter(recipe => matchesSearchTerm(recipe, searchTerm));
    }
    
    setFilteredRecipes(filtered);
  };

  // Smart-search trigger: when the local filter returns 0 results AND the user
  // typed at least 2 chars, ask the backend for diacritic-insensitive / fuzzy
  // matches + tags + aliases. Debounced 400ms to avoid spamming on every key.
  useEffect(() => {
    const trimmed = (searchTerm || '').trim();
    // Reset state up-front so old suggestions don't linger
    if (trimmed.length < 2 || filteredRecipes.length > 0 || showMyRecipes) {
      setSmartSuggestions([]);
      setSmartLowConfidence([]);
      setSmartAiEligible(false);
      setSmartLoading(false);
      return;
    }
    let cancelled = false;
    setSmartLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/recipes/smart-search`, {
          params: { q: trimmed },
        });
        if (cancelled) return;
        setSmartSuggestions(res.data.suggestions || []);
        setSmartLowConfidence(res.data.low_confidence || []);
        setSmartAiEligible(!!res.data.ai_eligible);
        setSmartSearchLogId(res.data.search_log_id || null);
      } catch (err) {
        if (!cancelled) {
          // Soft-fail — keep the regular zero-state visible
          setSmartSuggestions([]);
          setSmartLowConfidence([]);
          setSmartAiEligible(false);
          setSmartSearchLogId(null);
        }
      } finally {
        if (!cancelled) setSmartLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, [searchTerm, filteredRecipes.length, showMyRecipes]);

  // Ingredient filter functions
  const addIncludeIngredient = (ingredient) => {
    const trimmedIngredient = ingredient.trim().toLowerCase();
    if (trimmedIngredient && !includeIngredients.includes(trimmedIngredient)) {
      setIncludeIngredients([...includeIngredients, trimmedIngredient]);
    }
    setIngredientInput('');
    setShowIncludeSuggestions(false);
  };

  const handleIncludeKeyPress = (e) => {
    if (e.key === 'Enter' && ingredientInput.trim()) {
      e.preventDefault();
      addIncludeIngredient(ingredientInput);
    }
  };

  const removeIncludeIngredient = (ingredient) => {
    setIncludeIngredients(includeIngredients.filter(ing => ing !== ingredient));
  };

  const addExcludeIngredient = (ingredient) => {
    const trimmedIngredient = ingredient.trim().toLowerCase();
    if (trimmedIngredient && !excludeIngredients.includes(trimmedIngredient)) {
      setExcludeIngredients([...excludeIngredients, trimmedIngredient]);
    }
    setExcludeIngredientInput('');
    setShowExcludeSuggestions(false);
  };

  const handleExcludeKeyPress = (e) => {
    if (e.key === 'Enter' && excludeIngredientInput.trim()) {
      e.preventDefault();
      addExcludeIngredient(excludeIngredientInput);
    }
  };

  const removeExcludeIngredient = (ingredient) => {
    setExcludeIngredients(excludeIngredients.filter(ing => ing !== ingredient));
  };

  const clearAllFilters = () => {
    setIncludeIngredients([]);
    setExcludeIngredients([]);
  };

  // Allergen groups for quick filtering
  const allergenGroups = [
    {
      name: '🥜 Nødder',
      icon: '🥜',
      ingredients: ['hasselnød', 'mandel', 'pistacie', 'valnød', 'jordnød', 'peanut', 'sesamfrø', 'sesam']
    },
    {
      name: '🥛 Mælk',
      icon: '🥛',
      ingredients: ['mælk', 'fløde', 'smør', 'ost', 'yoghurt', 'skummetmælk', 'valle', 'kasein', 'laktose', 'cream']
    },
    {
      name: '🌾 Gluten',
      icon: '🌾',
      ingredients: ['hvede', 'byg', 'rug', 'havre', 'malt', 'gluten']
    },
    {
      name: '🍳 Æg',
      icon: '🍳',
      ingredients: ['æg', 'æggehvide', 'albumin', 'æggeblomme']
    },
    {
      name: '🍊 Citrus',
      icon: '🍊',
      ingredients: ['citron', 'lime', 'appelsin', 'grapefrugt', 'citrus']
    },
    {
      name: '🍓 Bær',
      icon: '🍓',
      ingredients: ['jordbær', 'hindbær', 'blåbær', 'brombær', 'ribs']
    }
  ];

  const addAllergenGroup = (group) => {
    const newExcludeList = [...new Set([...excludeIngredients, ...group.ingredients])];
    setExcludeIngredients(newExcludeList);
  };

  // Get filtered suggestions based on input
  const getIncludeSuggestions = () => {
    if (!ingredientInput) return availableIngredients.slice(0, 10);
    return availableIngredients
      .filter(ing => 
        ing.includes(ingredientInput.toLowerCase()) && 
        !includeIngredients.includes(ing)
      )
      .slice(0, 10);
  };

  const getExcludeSuggestions = () => {
    if (!excludeIngredientInput) return availableIngredients.slice(0, 10);
    return availableIngredients
      .filter(ing => 
        ing.includes(excludeIngredientInput.toLowerCase()) && 
        !excludeIngredients.includes(ing)
      )
      .slice(0, 10);
  };

  const types = [
    { value: 'klassisk', label: 'Klassisk', icon: 'klassisk.png' },
    { value: 'juice', label: 'Juice', icon: 'juice.png' },
    { value: 'smoothie', label: 'Smoothie', icon: 'smoothie.png' },
    { value: 'sodavand', label: 'Sodavand', icon: 'sodavand.png' },
    { value: 'cocktail', label: 'Cocktail', icon: 'cocktail.png' },
    { value: 'kaffe', label: 'Kaffe', icon: 'kaffe.png' },
    { value: 'sport', label: 'Sport', icon: 'sport.png' },
    { value: 'sukkerfri', label: 'Sukkerfri', icon: 'sukkerfri.png' },
    { value: 'maelk', label: 'Mælk', icon: 'maelk.png' }
  ];

  // ── Guest welcome popup + scroll-to-grid (per user 08-Jul-2026, v5) ──
  // Efter klik på "Se opskrifterne" på landingpagen → /recipes?welcome=1.
  // To ting sker samtidig:
  //  1. Velkomst-popup vises oven på opskriftssiden.
  //  2. Siden bag popuppen scrolles ned til selve opskriftsgrid'et,
  //     så når popuppen lukkes, ser brugeren opskrifterne i toppen af
  //     skærmen — ikke H1 + søgefelt + filtre.
  // Scroll'et sker KUN når ?welcome=1 er sat, aldrig ved direkte besøg.
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const recipesGridRef = useRef(null);
  const [pendingLandingScroll, setPendingLandingScroll] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('welcome') !== '1') return;
    setShowWelcomePopup(true);
    setPendingLandingScroll(true);
    // Ryd URL'en stille så refresh ikke gentager popup + scroll.
    const url = new URL(window.location.href);
    url.searchParams.delete('welcome');
    window.history.replaceState({}, '', url.pathname + url.search);
  }, []);

  // Kør scroll'et først når opskrifter er indlæst OG grid'et er mounted.
  // `scroll-margin-top` på ref-elementet håndterer nav-bar-højden så
  // opskrifterne står lige under den blå topbar (ikke gemt bagved).
  useEffect(() => {
    if (!pendingLandingScroll) return;
    if (loading) return;
    if (!recipesGridRef.current) return;
    const raf = requestAnimationFrame(() => {
      recipesGridRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
      setPendingLandingScroll(false);
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingLandingScroll, loading, filteredRecipes.length]);

  const dismissWelcomePopup = () => {
    setShowWelcomePopup(false);
  };

  return (
    <div className="space-y-6 fade-in" data-testid="recipes-page">
      {/* Onboarding Tour */}
      <OnboardingTooltip
        steps={getRecipesPageSteps()}
        currentStep={currentTourStep}
        onNext={handleTourNext}
        onSkip={handleTourSkip}
        onFinish={handleTourFinish}
      />

      {/* Velkomst-popup — vises oven på opskriftssiden efter klik på
          "Se opskrifterne" på landingpagen. Kun én gang pr. navigation
          (styret via ?welcome=1 der ryddes ved mount). */}
      {showWelcomePopup && (
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={dismissWelcomePopup}
          data-testid="guest-welcome-popup"
        >
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-center"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="welcome-popup-title"
          >
            <button
              type="button"
              onClick={dismissWelcomePopup}
              aria-label="Luk velkomsten"
              data-testid="guest-welcome-popup-close"
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1.5 transition-colors"
            >
              <FaTimes className="w-3.5 h-3.5" />
            </button>
            <div className="text-4xl mb-3">👋</div>
            <h2 id="welcome-popup-title" className="text-2xl font-bold text-gray-900 mb-3">
              Velkommen til SlushBook
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              Du udforsker nu SlushBook som gæst.<br />
              Kig frit rundt blandt opskrifterne.<br />
              Du kan altid <Link to="/signup" className="text-cyan-700 font-semibold underline hover:text-cyan-800">oprette en gratis konto</Link> senere.
            </p>
            <button
              type="button"
              onClick={dismissWelcomePopup}
              data-testid="guest-welcome-popup-cta"
              className="w-full py-3 rounded-xl font-bold text-white text-base shadow-md transition-all hover:scale-[1.02] active:scale-100"
              style={{
                background: 'linear-gradient(90deg, #14b8a6 0%, #0891b2 50%, #2563eb 100%)',
              }}
            >
              Begynd at udforske
            </button>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-4xl font-bold mb-2">{t('recipes.title')}</h1>
        <p className="text-gray-600">{t('recipes.browseCount', { count: recipes.length })}</p>
        {newCount > 0 && (() => {
          const sinceLabel = frozenSince
            ? new Date(frozenSince).toLocaleDateString('da-DK', {
                day: 'numeric',
                month: 'short',
              })
            : null;
          return (
            <div
              className="mt-3 flex flex-wrap items-center gap-3"
              data-testid="new-recipes-banner"
            >
              <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold px-3 py-1.5 rounded-full">
                <span aria-hidden>✨</span>
                <span data-testid="new-recipes-count">
                  {newCount} {newCount === 1 ? 'ny opskrift' : 'nye opskrifter'}
                  {sinceLabel ? ` siden ${sinceLabel}` : ' siden sidste besøg'}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setShowNewOnly(v => !v)}
                data-testid="toggle-new-only-filter"
                aria-pressed={showNewOnly}
                className={`text-sm font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  showNewOnly
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                }`}
              >
                {showNewOnly ? '✓ Viser kun nye' : 'Vis kun nye'}
              </button>
            </div>
          );
        })()}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        {/* Search row — input + alcohol filter + "Filtre" toggle on one line.
            Wraps gracefully on very narrow screens. Type, ingrediens, sortering
            og "mine opskrifter" lever bag Filtre-knappen. */}
        <div className="flex flex-wrap gap-3 items-stretch mb-4">
          <div className="relative flex-1" data-tour="search-bar">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              data-testid="search-input"
              placeholder={t('recipes.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                data-testid="clear-search"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            )}
          </div>
          {/* Alkohol Filter - inline with Søg + Filtre, ALWAYS visible */}
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-xl border border-gray-200 whitespace-nowrap">
            <button
              onClick={() => setAlcoholFilter('none')}
              data-testid="alcohol-filter-none"
              className={`text-sm font-medium transition-colors ${alcoholFilter === 'none' ? 'text-gray-900 font-bold' : 'text-gray-400'}`}
            >
              {t('recipes.withoutAlcohol')}
            </button>
            <button
              onClick={() => setAlcoholFilter(alcoholFilter === 'none' ? 'only' : 'none')}
              data-testid="alcohol-toggle"
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                alcoholFilter === 'only' ? 'bg-red-500' : 'bg-green-500'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-all ${
                  alcoholFilter === 'only' ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
            <button
              onClick={() => setAlcoholFilter('only')}
              data-testid="alcohol-filter-only"
              className={`text-sm font-medium transition-colors ${alcoholFilter === 'only' ? 'text-gray-900 font-bold' : 'text-gray-400'}`}
            >
              {t('recipes.withAlcohol')}
            </button>
          </div>
          {(() => {
            // Count active filters HIDDEN behind the Filtre-button (alcohol is
            // inline so it doesn't need a badge — user can see it directly).
            const activeFilterCount =
              (typeFilter ? 1 : 0) +
              (sortBy !== 'default' && user?.role === 'admin' ? 1 : 0) +
              (showMyRecipes ? 1 : 0) +
              (includeIngredients.length > 0 ? 1 : 0) +
              (excludeIngredients.length > 0 ? 1 : 0);
            return (
              <button
                type="button"
                onClick={() => setMobileFiltersOpen((v) => !v)}
                aria-expanded={mobileFiltersOpen}
                aria-controls="recipes-filter-panel"
                data-testid="filters-toggle"
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 bg-white hover:border-cyan-300 transition-colors font-medium text-gray-800 whitespace-nowrap"
              >
                <FaFilter className="text-cyan-500" />
                <span>{t('recipes.filtersButton', 'Filtre')}</span>
                {activeFilterCount > 0 && (
                  <span
                    data-testid="filters-active-count"
                    className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-cyan-500 text-white text-xs font-bold"
                  >
                    {activeFilterCount}
                  </span>
                )}
                {mobileFiltersOpen
                  ? <FaChevronUp className="text-gray-500" />
                  : <FaChevronDown className="text-gray-500" />}
              </button>
            );
          })()}
        </div>

        {/* Filter panel — collapsed on EVERY screen size by default. Wraps
            all filter sections including alcohol and type. */}
        <div
          id="recipes-filter-panel"
          data-testid="recipes-filter-panel"
          className={mobileFiltersOpen ? 'block' : 'hidden'}
        >

        {/* Sortering - Only for Admin */}
        {user?.role === 'admin' && (
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FaSortAlphaDown className="inline mr-2" />
              {t('recipes.sorting')}
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
            >
              <option value="default">{t('recipes.sortDefault')}</option>
              <option value="alphabetical">{t('recipes.sortAlphabetical')}</option>
              <option value="rating">{t('recipes.sortRating')}</option>
            </select>
          </div>
        )}

        {/* Mine Opskrifter Filter */}
        <div className="mb-4">
          <button
            onClick={() => setShowMyRecipes(!showMyRecipes)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
              showMyRecipes 
                ? 'bg-blue-500 text-white border-blue-500' 
                : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
            }`}
          >
            <FaFilter />
            <span className="font-medium">
              {showMyRecipes ? t('recipes.myRecipes') : t('recipes.showMyRecipes')}
            </span>
          </button>
        </div>

        {/* Ingredient Filters - Collapsible */}
        <div className="mb-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl border-2 border-cyan-200 shadow-sm overflow-hidden">
          {/* Clickable Header */}
          <button
            onClick={() => setIsIngredientFilterExpanded(!isIngredientFilterExpanded)}
            className="w-full flex items-center justify-between p-4 hover:bg-cyan-100/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">🔍</span>
              <h3 className="text-sm font-semibold text-gray-800">{t('recipes.ingredientFilter')}</h3>
              {(includeIngredients.length > 0 || excludeIngredients.length > 0) && (
                <span className="bg-cyan-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  {includeIngredients.length + excludeIngredients.length} {t('common.filter')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(includeIngredients.length > 0 || excludeIngredients.length > 0) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAllFilters();
                  }}
                  className="text-xs text-cyan-600 hover:text-cyan-800 font-medium bg-white px-2 py-1 rounded-md mr-2"
                >
                  {t('recipes.clearAll')}
                </button>
              )}
              {isIngredientFilterExpanded ? (
                <FaChevronUp className="text-gray-600" />
              ) : (
                <FaChevronDown className="text-gray-600" />
              )}
            </div>
          </button>

          {/* Expandable Content */}
          {isIngredientFilterExpanded && (
            <div className="p-5 pt-0 space-y-4 border-t border-cyan-200">

              {/* Quick Allergen Tags */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              ⚡ {t('recipes.quickExcludeLabel')}
            </label>
            <div className="flex flex-wrap gap-2">
              {allergenGroups.map((group, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => addAllergenGroup(group)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-300 rounded-lg text-xs font-medium text-gray-700 hover:text-orange-700 transition-all shadow-sm"
                  title={`${t('recipes.excludeAllergen')}: ${group.ingredients.join(', ')}`}
                >
                  <span>{group.icon}</span>
                  <span>{group.name.replace(/🥜|🥛|🌾|🍳|🍊|🍓/g, '').trim()}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Include Ingredients */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              ✅ {t('recipes.mustIncludeLabel')}
            </label>
            <input
              type="text"
              value={ingredientInput}
              onChange={(e) => {
                setIngredientInput(e.target.value);
                setShowIncludeSuggestions(true);
              }}
              onKeyPress={handleIncludeKeyPress}
              onFocus={() => setShowIncludeSuggestions(true)}
              onBlur={() => setTimeout(() => setShowIncludeSuggestions(false), 200)}
              placeholder={t('recipes.ingredientPlaceholder')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            
            {/* Dropdown suggestions */}
            {showIncludeSuggestions && getIncludeSuggestions().length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {getIncludeSuggestions().map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => addIncludeIngredient(suggestion)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            
            {includeIngredients.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {includeIngredients.map((ing, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium"
                  >
                    {ing}
                    <button
                      onClick={() => removeIncludeIngredient(ing)}
                      className="hover:text-green-900"
                    >
                      <FaTimes size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Exclude Ingredients */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              ❌ {t('recipes.mustNotIncludeLabel')}
            </label>
            <input
              type="text"
              value={excludeIngredientInput}
              onChange={(e) => {
                setExcludeIngredientInput(e.target.value);
                setShowExcludeSuggestions(true);
              }}
              onKeyPress={handleExcludeKeyPress}
              onFocus={() => setShowExcludeSuggestions(true)}
              onBlur={() => setTimeout(() => setShowExcludeSuggestions(false), 200)}
              placeholder={t('recipes.excludeIngredientPlaceholder')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            
            {/* Dropdown suggestions */}
            {showExcludeSuggestions && getExcludeSuggestions().length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {getExcludeSuggestions().map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => addExcludeIngredient(suggestion)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            
            {excludeIngredients.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {excludeIngredients.map((ing, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"
                  >
                    {ing}
                    <button
                      onClick={() => removeExcludeIngredient(ing)}
                      className="hover:text-red-900"
                    >
                      <FaTimes size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Results count */}
          {(includeIngredients.length > 0 || excludeIngredients.length > 0) && (
            <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
              📊 {t('recipes.showingWithFilters', { count: filteredRecipes.length })}
            </div>
          )}
            </div>
          )}
        </div>

        {/* Type Filter — now also behind the Filtre-button */}
        <div data-tour="type-filter">
          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('recipes.typeLabel')}</label>
          
          {/* Mobile: Dropdown */}
          <div className="md:hidden">
            <div className="relative">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-4 py-3 pr-10 bg-white border border-gray-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500 font-medium"
              >
                <option value="">{t('recipes.allTypes')}</option>
                {types.map((type) => (
                  <option key={type.value} value={type.value}>
                    {t(`recipes.${type.value}`, type.label)}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {typeFilter && (
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                <img 
                  src={`/icons/${types.find(t => t.value === typeFilter)?.icon}`} 
                  alt={types.find(t => t.value === typeFilter)?.label}
                  className="w-5 h-5 rounded-full"
                />
                <span>{t('recipes.filtered')}: {t(`recipes.${typeFilter}`, types.find(t => t.value === typeFilter)?.label)}</span>
              </div>
            )}
          </div>

          {/* Desktop: Buttons */}
          <div className="hidden md:flex flex-wrap gap-2">
            <button
              onClick={() => setTypeFilter('')}
              data-testid="type-filter-all"
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                typeFilter === ''
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('common.all')}
            </button>
            {types.map((type) => (
              <button
                key={type.value}
                data-testid={`type-filter-${type.value}`}
                onClick={() => setTypeFilter(type.value)}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                  typeFilter === type.value
                    ? 'bg-cyan-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <img 
                  src={`/icons/${type.icon}`} 
                  alt={type.label}
                  className="w-6 h-6 rounded-full"
                />
                {t(`recipes.${type.value}`, type.label)}
              </button>
            ))}
          </div>
        </div>
        </div>{/* /recipes-filter-panel — wraps every filter except search + alcohol */}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="empty-state space-y-6" data-testid="recipes-empty-state">
          <div className="text-center">
            <div className="empty-state-icon">🔍</div>
            <h3 className="text-xl font-bold mb-2">{t('recipes.noRecipes')}</h3>
            <p className="text-gray-600">{t('recipes.tryDifferentFilters', 'Prøv at ændre dine filtre eller søgeord')}</p>
          </div>

          {smartLoading && (
            <div className="text-center text-sm text-gray-500" data-testid="smart-search-loading">
              <span className="inline-block animate-spin mr-2">⏳</span>
              Søger efter lignende opskrifter…
            </div>
          )}

          {!smartLoading && smartSuggestions.length > 0 && (
            <div
              className="mx-auto max-w-2xl bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-5"
              data-testid="smart-search-suggestions"
            >
              <p className="text-sm font-semibold text-blue-900 mb-3">
                💡 Mente du en af disse?
              </p>
              <div className="flex flex-wrap gap-2">
                {smartSuggestions.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => navigate(`/recipes/${r.id}`)}
                    data-testid={`smart-search-suggestion-${r.id}`}
                    className={`inline-flex items-center gap-1.5 border text-sm px-3 py-1.5 rounded-full transition ${
                      r.is_own
                        ? 'bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-900'
                        : 'bg-white hover:bg-blue-100 border-blue-300 text-blue-900'
                    }`}
                  >
                    {r.is_own && <span className="text-[10px]" title="Din egen opskrift">👤</span>}
                    <span className="font-medium">{r.name}</span>
                    <span className={`text-[10px] ${r.is_own ? 'text-amber-500' : 'text-blue-500'}`}>{r.match_score}%</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!smartLoading && smartSuggestions.length === 0 && smartLowConfidence.length > 0 && (
            <div className="mx-auto max-w-2xl text-center" data-testid="smart-search-low-confidence">
              <p className="text-xs text-gray-500 mb-2">Måske disse?</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {smartLowConfidence.slice(0, 3).map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => navigate(`/recipes/${r.id}`)}
                    className="text-xs text-gray-600 underline hover:text-gray-800"
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!smartLoading && smartAiEligible && (
            <div
              className="mx-auto max-w-md text-center bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5"
              data-testid="smart-search-ai-cta"
            >
              <p className="text-sm text-gray-700 mb-3">
                {(smartSuggestions.length > 0 || smartLowConfidence.length > 0) ? (
                  <>
                    Var det ikke det rigtige? Lad AI lave en helt ny opskrift baseret på{' '}
                    <span className="font-semibold">&quot;{searchTerm}&quot;</span>.
                  </>
                ) : (
                  <>
                    Ingen opskrifter passer til{' '}
                    <span className="font-semibold">&quot;{searchTerm}&quot;</span>. Lad AI lave et forslag.
                  </>
                )}
              </p>
              <button
                type="button"
                onClick={() => {
                  // Mark the empty-search row as having converted into an
                  // AI request — used by the admin "Populære søgninger uden
                  // resultat" page to see which gaps actually motivate users
                  // to ask for AI generation. Fire-and-forget — never blocks
                  // opening the modal.
                  if (smartSearchLogId) {
                    axios
                      .post(`${API}/search-logs/${smartSearchLogId}/ai-clicked`)
                      .catch(() => {});
                  }
                  setAiModalOpen(true);
                }}
                data-testid="smart-search-ai-button"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-5 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
              >
                🤖 Generer opskrift med AI
                {!hasPremiumAccess(user) && (
                  <span
                    data-testid="smart-search-ai-lock"
                    className="text-[10px] bg-white/25 px-2 py-0.5 rounded-full"
                    title="Kræver Light eller Pro"
                  >
                    🔒 Medlem
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div ref={recipesGridRef} style={{ scrollMarginTop: '80px' }}>
          <p className="text-sm text-gray-600 mb-4">
            {filteredRecipes.length !== 1 ? t('recipes.showingCountPlural', { count: filteredRecipes.length }) : t('recipes.showingCount', { count: filteredRecipes.length })}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {/* Add Recipe Card - First in grid. Visible for guests too — they
                can create up to 2 private recipes; the limit and Pro upsell
                are enforced inside AddRecipePage. */}
            <div
              data-tour="add-recipe-card"
              data-testid="recipes-add-card"
              onClick={() => navigate('/add-recipe')}
              className="flex flex-col bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all overflow-hidden border-2 border-dashed border-purple-300 hover:border-purple-500 cursor-pointer hover:scale-105"
            >
              {/* Header matching RecipeCard image height */}
              <div className="h-64 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <div className="bg-white rounded-full p-6 shadow-md">
                  <FaPlus className="text-5xl text-purple-600" />
                </div>
              </div>

              {/* Content matching RecipeCard */}
              <div className="p-5 flex flex-col flex-grow justify-center">
                <h3 className="font-bold text-lg text-center text-purple-700">
                  {t('recipes.addYourOwnRecipe')}
                </h3>
                <p className="text-sm text-gray-600 text-center mt-2">
                  {(!hasPremiumAccess(user))
                    ? t('recipes.guestAddHint', 'Gratis: op til 2 private opskrifter')
                    : t('recipes.createAndShare')}
                </p>
              </div>
            </div>

            {(() => {
              // Jævn fordeling af sponsor-rækker gennem opskriftsgriddet:
              //  • Antal sponsorer K = presentedSponsoredRows.length
              //  • Antal opskrifter N = filteredRecipes.length
              //  • Position i (0-indexed) = round((i+1)·N / (K+1)) − 1
              //    →  K=1: midten. K=2: 1/3 og 2/3. K=3: 1/4, 2/4, 3/4. …
              //  • Rækkefølgen er allerede roteret pr. sidevisning i
              //    `presentedSponsoredRows` — ingen ny beregning her.
              //  • Skjules helt hvis brugeren ikke må se reklamer eller der
              //    ikke findes aktive sponsor-rækker.
              const children = [];
              const rowsPool = shouldShowAds ? presentedSponsoredRows : [];
              const totalRecipes = filteredRecipes.length;
              // Cap antal sponsor-visninger så vi aldrig placerer flere end
              // (opskrifter − 1); ellers kan to sponsorer havne lige efter
              // hinanden når listen er meget kort.
              const sponsorCount = Math.min(rowsPool.length, Math.max(0, totalRecipes - 1));
              const positionToRow = new Map();
              if (sponsorCount > 0 && totalRecipes > 0) {
                for (let i = 0; i < sponsorCount; i++) {
                  const raw = Math.round(((i + 1) * totalRecipes) / (sponsorCount + 1)) - 1;
                  const clamped = Math.max(0, Math.min(raw, totalRecipes - 2));
                  // Undgå at to sponsorer lander på samme position (kan ske
                  // med afrunding på meget korte lister). Skub næste plads
                  // hvis kollision opstår.
                  let pos = clamped;
                  while (positionToRow.has(pos) && pos < totalRecipes - 2) {
                    pos += 1;
                  }
                  positionToRow.set(pos, rowsPool[i]);
                }
              }

              filteredRecipes.forEach((recipe, idx) => {
                children.push(
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    sessionId={sessionId}
                    onLockedClick={() => setShowUpgradeModal(true)}
                    isNew={isNewRecipe(recipe)}
                  />
                );
                if (positionToRow.has(idx)) {
                  const row = positionToRow.get(idx);
                  children.push(
                    <SponsoredRowBlock
                      key={`sponsored-row-${idx}-${row.id || idx}`}
                      row={row}
                    />
                  );
                }
              });
              return children;
            })()}
          </div>
          {/* Fast platform-CTA nederst — vises kun for brugere der ser reklamer */}
          {shouldShowAds && <AdvertiseYourselfCTA />}
        </div>
      )}
      
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason="recipe_locked"
        currentUser={user}
        requiredTier="light"
      />

      <AIRecipeGeneratorModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        query={searchTerm}
        user={user}
        onSavedPrivate={(rec) => navigate(`/recipes/${rec.id}`)}
        onSentToSandbox={() => navigate('/recipes')}
      />
    </div>
  );
};

export default RecipesPage;